import React, { useState, useEffect } from 'react';
import {
  RotateCcw,
  Power,
  RefreshCw,
  Lock,
  Unlock,
  AlertTriangle,
  Unplug,
  ShieldAlert,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { pushNotify } from '../../services/websocketService';
import { toastService } from '../../services/toastService';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
import { usableTerminalCode, formatExpiry } from './TerminalNewCodeModal';
import WelcomeSetupCard from './WelcomeSetupCard';
interface Props {
  terminal: { terminalId: string; locationId: string; registerName: string; locationName?: string; provisionCode?: string; provisionCodeMasked?: boolean; codeExpired?: boolean };
  tenantId: string;
  /** Called after a successful deregister so the page can refetch its status. */
  onDeregistered?: () => void | Promise<void>;
}

// Deregister stages — same flow as the merchant app (platform-app PosTerminalDetail):
//   warning → countdown (10s, cancellable) → sending → sent | error
type DeregStage = 'idle' | 'warning' | 'countdown' | 'sending' | 'sent' | 'error';

const ACTIONS = [
  {
    id: 'refresh',
    label: 'Refresh',
    description: 'Clear all state and return to PIN login. No page reload.',
    icon: RefreshCw,
    color: 'emerald',
    wsType: 'terminal:refresh',
  },
  {
    id: 'restart',
    label: 'Restart',
    description: 'Full page reload. Re-fetches all data and reconnects WebSocket.',
    icon: RotateCcw,
    color: 'blue',
    wsType: 'terminal:restart',
  },
  {
    id: 'lock',
    label: 'Lock',
    description: 'Lock the terminal. No one can use it until unlocked by admin.',
    icon: Lock,
    color: 'amber',
    wsType: 'terminal:lock',
  },
  {
    id: 'shutdown',
    label: 'Shut Down',
    description: 'Close the POS application. Requires someone to physically reopen.',
    icon: Power,
    color: 'red',
    wsType: 'terminal:shutdown',
  },
] as const;

const colorMap: Record<string, { bg: string; border: string; iconBg: string; iconText: string; btnBg: string; btnHover: string }> = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', btnBg: 'bg-emerald-600', btnHover: 'hover:bg-emerald-700' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-100', iconText: 'text-blue-600', btnBg: 'bg-blue-600', btnHover: 'hover:bg-blue-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-100', iconText: 'text-amber-600', btnBg: 'bg-amber-600', btnHover: 'hover:bg-amber-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-100', iconText: 'text-red-600', btnBg: 'bg-red-600', btnHover: 'hover:bg-red-700' },
};

const PosControlTab: React.FC<Props> = ({ terminal, tenantId, onDeregistered }) => {
  const [sending, setSending] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<typeof ACTIONS[number] | null>(null);
  const [deregStage, setDeregStage] = useState<DeregStage>('idle');
  const [deregCountdown, setDeregCountdown] = useState(10);
  const [deregError, setDeregError] = useState<string | null>(null);
  /* The full code comes back from deregister — the list only has it masked once the till was activated. */
  const [deregCode, setDeregCode] = useState<{ code: string; expiresAt: string | null } | null>(null);
  const listCode = usableTerminalCode(terminal);

  const closeDereg = () => { setDeregStage('idle'); setDeregCountdown(10); setDeregError(null); };

  // Countdown runs in the admin's browser — the abort window before the push fires.
  useEffect(() => {
    if (deregStage !== 'countdown') return;
    const id = window.setInterval(() => {
      setDeregCountdown((n) => {
        if (n <= 1) {
          window.clearInterval(id);
          setTimeout(() => setDeregStage('sending'), 0);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [deregStage]);

  // Two independent writes: `terminal:revoke` tells the live device to clear its
  // provisioning + reload to the code-entry screen; the API flips the provision
  // row back to 'pending' so the SAME code can be re-entered.
  useEffect(() => {
    if (deregStage !== 'sending') return;
    void (async () => {
      try {
        const [, resp] = await Promise.all([
          pushNotify('terminal:revoke', {
            tenantId,
            locationId: terminal.locationId,
            terminalId: terminal.terminalId,
          }, { source: 'super_admin' }),
          api.posTerminals.deregister(terminal.terminalId, tenantId),
        ]);
        const full = usableTerminalCode({ provisionCode: (resp as any)?.provisionCode });
        setDeregCode(full ? { code: full, expiresAt: (resp as any)?.codeExpiresAt || null } : null);
        try { await onDeregistered?.(); } catch { /* non-fatal — status just stays stale */ }
        setDeregStage('sent');
      } catch (err: any) {
        setDeregError(err?.message || 'Failed to send command — check the network');
        setDeregStage('error');
      }
    })();
  }, [deregStage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (action: typeof ACTIONS[number]) => {
    setSending(action.id);
    try {
      // For lock/shutdown, persist state to register record
      if (action.id === 'lock' || action.id === 'shutdown') {
        await api.posConfig.updateRegister(terminal.terminalId, {
          tenantId,
          terminalControl: {
            status: action.id === 'lock' ? 'locked' : 'shutdown',
            lockedBy: 'super_admin',
            lockedAt: Date.now(),
          },
        });
      }

      // Push WebSocket command to POS
      await pushNotify(action.wsType, {
        tenantId,
        locationId: terminal.locationId,
        terminalId: terminal.terminalId,
      }, { source: 'super_admin' });

      setConfirmAction(null);
      toastService.success(`${action.label} command sent to ${terminal.registerName}`);
    } catch (err: any) {
      toastService.error(`Failed: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(null);
    }
  };

  const handleUnlock = async () => {
    setSending('unlock');
    try {
      await api.posConfig.updateRegister(terminal.terminalId, {
        tenantId,
        terminalControl: { status: 'active', lockedBy: null, lockedAt: null },
      });
      await pushNotify('terminal:unlock', {
        tenantId,
        locationId: terminal.locationId,
        terminalId: terminal.terminalId,
      }, { source: 'super_admin' });
      toastService.success(`${terminal.registerName} unlocked`);
    } catch (err: any) {
      toastService.error(`Failed: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* "Hello from Ruby" — Fresh / Set up, the shipping lock, and this device's progress. */}
      <WelcomeSetupCard registerId={terminal.terminalId} tenantId={tenantId} locationId={terminal.locationId} locationName={terminal.locationName} />

      {/* Unlock button (always visible for convenience) */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <Unlock size={16} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-stone-800 text-sm font-semibold">Unlock Terminal</p>
              <p className="text-stone-500 text-xs mt-0.5">Remove lock or shutdown state and restore POS access</p>
            </div>
          </div>
          <button
            onClick={handleUnlock}
            disabled={sending === 'unlock'}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {sending === 'unlock' ? <RubyGemMark size={14} drawing color="currentColor" /> : <Unlock size={14} />}
            Unlock
          </button>
        </div>
      </div>

      {/* Control Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-stone-800 font-semibold text-sm mb-1">Terminal Control</h3>
        <p className="text-stone-500 text-xs mb-4">
          Send commands to <span className="font-medium text-stone-600">{terminal.registerName}</span>. POS will show a 15-second countdown before executing.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ACTIONS.map((action) => {
            const Icon = action.icon;
            const c = colorMap[action.color];
            return (
              <div key={action.id} className={`rounded-xl border p-4 ${c.bg} ${c.border}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${c.iconBg}`}>
                    <Icon size={16} className={c.iconText} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-800 text-sm font-semibold">{action.label}</p>
                    <p className="text-stone-500 text-xs mt-0.5">{action.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => setConfirmAction(action)}
                  disabled={sending !== null}
                  className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white ${c.btnBg} ${c.btnHover} disabled:opacity-50 transition-colors`}
                >
                  <Icon size={14} />
                  {action.label}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recovery & provisioning */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-stone-800 font-semibold text-sm mb-1">Recovery &amp; provisioning</h3>
        <p className="text-stone-500 text-xs mb-4">Reset the device&apos;s saved provisioning.</p>
        <div className="rounded-xl border p-4 bg-red-50 border-red-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-red-100">
              <Unplug size={16} className="text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-stone-800 text-sm font-semibold flex items-center gap-2">
                Deregister Terminal
                <span className="text-[10px] font-bold tracking-wide text-red-700 bg-red-100 border border-red-200 rounded px-1.5 py-0.5">DESTRUCTIVE</span>
              </p>
              <p className="text-stone-500 text-xs mt-0.5">
                {listCode
                  ? `The device's saved provisioning will be cleared. Code ${listCode} can be entered to bring it back online.`
                  : "The device's saved provisioning will be cleared. Its activation code is shown when this is done, valid for 14 days."}
              </p>
            </div>
          </div>
          <button
            onClick={() => { setDeregError(null); setDeregStage('warning'); }}
            disabled={sending !== null || deregStage !== 'idle'}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors shrink-0"
          >
            <Unplug size={14} />
            Deregister
          </button>
        </div>
      </div>

      {/* Deregister modal */}
      {deregStage !== 'idle' && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-[min(480px,92vw)] overflow-hidden">
            {deregStage !== 'sending' && (
              <div className="flex items-start gap-3 px-6 pt-6 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${deregStage === 'sent' ? 'bg-emerald-100' : deregStage === 'error' ? 'bg-amber-100' : 'bg-red-100'}`}>
                  {deregStage === 'sent'
                    ? <Unplug size={20} className="text-emerald-600" />
                    : <ShieldAlert size={20} className={deregStage === 'error' ? 'text-amber-600' : 'text-red-600'} />}
                </div>
                <div className="min-w-0">
                  <p className="text-stone-800 font-semibold leading-tight">
                    {deregStage === 'warning' && 'Deregister this terminal?'}
                    {deregStage === 'countdown' && 'Sending in…'}
                    {deregStage === 'sent' && 'Deregister command sent'}
                    {deregStage === 'error' && 'Could not send command'}
                  </p>
                  <p className="text-stone-500 text-xs mt-0.5 truncate">
                    {terminal.registerName}{terminal.locationName ? ` · ${terminal.locationName}` : ''}
                  </p>
                </div>
              </div>
            )}

            {deregStage === 'warning' && (
              <div className="px-6 pb-6">
                <div className="text-[13px] text-stone-600 leading-relaxed space-y-3">
                  <p>This will send a remote command that:</p>
                  <ul className="list-disc list-outside pl-5 space-y-1">
                    <li>Clears the terminal&apos;s saved <span className="font-semibold text-stone-800">provisioning, device token, and PIN session</span></li>
                    <li>Disconnects the WebSocket and reloads the app</li>
                    <li>Returns the terminal to the &quot;Enter Provisional Code&quot; screen</li>
                  </ul>
                  <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5">
                    <p className="text-red-700 font-semibold text-[13px]">Any in-progress sale on the terminal will be lost.</p>
                    <p className="text-stone-500 text-xs mt-0.5">Make sure the cashier isn&apos;t mid-transaction.</p>
                  </div>
                  <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5">
                    <p className="text-stone-500 text-xs">To bring it back online, enter its activation code on the terminal.</p>
                    {listCode && <p className="font-mono font-bold text-stone-900 text-base tracking-widest mt-1">{listCode}</p>}
                    <p className="text-stone-400 text-[11px] mt-1">The code is shown when this is done. It works once and expires in 14 days.</p>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={closeDereg} className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={() => { setDeregCountdown(10); setDeregStage('countdown'); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    <ShieldAlert size={14} />
                    Continue
                  </button>
                </div>
              </div>
            )}

            {deregStage === 'countdown' && (
              <div className="px-6 pb-6">
                <div className="py-2 flex flex-col items-center">
                  <div className="text-[88px] font-extrabold text-red-600 tabular-nums leading-none">{deregCountdown}</div>
                  <p className="text-stone-600 text-[13px] mt-3 text-center leading-relaxed">
                    Cancel any time before the countdown reaches zero. Once it&apos;s zero, the command is sent and the terminal reloads immediately.
                  </p>
                </div>
                <div className="mt-4 flex justify-center">
                  <button onClick={closeDereg} className="px-5 py-2.5 min-h-[44px] rounded-lg text-sm font-bold text-stone-700 bg-stone-100 hover:bg-stone-200 transition-colors">
                    Cancel deregister
                  </button>
                </div>
              </div>
            )}

            {deregStage === 'sending' && (
              <RubyLoader variant="section" label="Sending deregister command…" className="py-10" />
            )}

            {deregStage === 'sent' && (
              <div className="px-6 pb-6">
                <p className="text-[13px] text-stone-600 leading-relaxed">
                  The terminal has been told to clear its session. It will show the provisional-code entry screen and is ready for sign-in.
                </p>
                {deregCode ? (
                  <div className="mt-3 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5">
                    <p className="text-stone-500 text-xs">Activation code — enter it on the terminal:</p>
                    <p className="font-mono font-bold text-stone-900 text-base tracking-widest mt-1 select-all">{deregCode.code}</p>
                    {deregCode.expiresAt && <p className="text-stone-400 text-[11px] mt-1">Works once · expires {formatExpiry(deregCode.expiresAt)}</p>}
                  </div>
                ) : (
                  <p className="mt-3 text-[12px] text-stone-500">No activation code came back. Use &quot;Generate new code&quot; on the Terminals list to issue one.</p>
                )}
                <div className="mt-5 flex justify-end">
                  <button onClick={closeDereg} className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors">
                    Done
                  </button>
                </div>
              </div>
            )}

            {deregStage === 'error' && (
              <div className="px-6 pb-6">
                <div className="text-[13px] text-stone-600 leading-relaxed space-y-2">
                  <p>{deregError || 'The deregister command could not be delivered. The terminal still has its old provisioning.'}</p>
                  <p className="text-stone-400 text-xs">It&apos;s safe to retry.</p>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={closeDereg} className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 transition-colors">
                    Close
                  </button>
                  <button
                    onClick={() => { setDeregError(null); setDeregStage('warning'); }}
                    className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setConfirmAction(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[confirmAction.color].iconBg}`}>
                <AlertTriangle size={20} className={colorMap[confirmAction.color].iconText} />
              </div>
              <div>
                <p className="text-stone-800 font-semibold">Confirm {confirmAction.label}</p>
                <p className="text-stone-500 text-xs mt-0.5">{terminal.registerName}</p>
              </div>
            </div>
            <p className="text-stone-600 text-sm mb-5">
              {confirmAction.id === 'lock' && 'This will lock the POS terminal. No one can use it until unlocked.'}
              {confirmAction.id === 'shutdown' && 'This will shut down the POS terminal. Someone must physically reopen the browser.'}
              {confirmAction.id === 'restart' && 'This will force a full page reload on the POS terminal.'}
              {confirmAction.id === 'refresh' && 'This will clear all POS state and return to PIN login.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(confirmAction)}
                disabled={sending !== null}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white ${colorMap[confirmAction.color].btnBg} ${colorMap[confirmAction.color].btnHover} disabled:opacity-50 transition-colors`}
              >
                {sending ? <RubyGemMark size={14} drawing color="currentColor" /> : null}
                {sending ? 'Sending...' : `Yes, ${confirmAction.label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosControlTab;
